import { PaginatedSearchRequest } from "../../../../_shared/models/paginated-search-request.model";
import { AssetType } from "../../../_shared/enums/asset-type.enum";
import { ComplianceResult } from "../../_shared/enums/compliance-result.enum";
import { ComplianceTasksTableAction } from "../../_shared/enums/compliance-tasks-table-action.enum";
import { RoutineChecklistRequirementLinkedDocument } from "./inspection-checklist.model";

export type ComplianceTab = 'complianceOverviewDetails' | 'periodicComplianceTasks' | 'routineComplianceTasks';

export interface ComplianceOverviewRequirementsRequest extends PaginatedSearchRequest {
  accountIds: Array<number>;
  complianceRegion?: ComplianceRegion;
  complianceType?: ComplianceType;
  assetTypeId?: AssetType;
  complianceId?: number;
}

export interface ComplianceOverviewRequirementsApiRequest {
  accountIds: Array<number>;
  page: number;
  pageSize: number;
  searchTerm: string | null;
  complianceId: number | null;
  regionId: number | null;
  countryId: number | null;
}

export interface ComplianceOverviewRequirementAssetsApiRequest {
  accountIds: Array<number>;
  page: number;
  pageSize: number;
  searchTerm: string | null;
  complianceRequirementId: number;
  assetTypeId?: AssetType;
}

export interface ComplianceAssetsRequest extends ComplianceOverviewRequirementsRequest {
  complianceId: number;
}

export type ComplianceOverviewRequirement = {
  requirementId: number;
  requirementName: string;
  complianceId: number;
  complianceName: string;
  assetCount: number;
  compliantCount?: number;
  expiringSoonCount?: number;
  nonCompliantCount?: number;
}

export type ComplianceOverviewRequirementAsset = {
  assetId: number;
  assetName: string;
  complianceId: number;
  complianceName: string;
  documentAttachmentId?: number | null;
  documentName?: string | null;
  linkedDocument?: RoutineChecklistRequirementLinkedDocument | null;
  lastInspectionDate?: string | Date | null;
  issuedDate?: string | Date | null;
  expiryDate?: string | Date | null;
  status: string;
  daysOverdue: number;
}

interface ComplianceCalendarApiRequestBase {
  accountIds: Array<number>;
  timeFrom: Date;
  timeTo: Date;
  statuses: Array<string>;
  calendarEventTypes: Array<string>;
  searchTerm: string;
  complianceId: number | null;
  regionId: number | null;
  countryId: number | null;
}

export type ComplianceCalendarEventsPreviewApiRequest = ComplianceCalendarApiRequestBase;

export interface ComplianceCalendarEventsByDateApiRequest extends ComplianceCalendarApiRequestBase {
  date: Date;
  endOfDay: Date;
}

export interface TodayComplianceCalendarEventsApiRequest extends ComplianceCalendarApiRequestBase {
  page: number;
  pageSize: number;
}

export type CompliancePhase = 'pre' | 'post';

export type ComplianceCalendarEventType =
  | 'pre-trip'
  | 'post-trip'
  | 'pre-work-order'
  | 'post-work-order'
  | 'periodic'
  | 'unknown';

export const COMPLIANCE_CALENDAR_EVENT_TYPE_LABELS: Record<ComplianceCalendarEventType, string> = {
  'pre-trip': 'Pre-trip',
  'post-trip': 'Post-trip',
  'pre-work-order': 'Pre-work order',
  'post-work-order': 'Post-work order',
  periodic: 'Periodic',
  unknown: ''
};

export const COMPLIANCE_CALENDAR_EVENT_TYPE_LABEL_KEYS: Record<ComplianceCalendarEventType, string | null> = {
  'pre-trip': 'compliance.overview.calendar.eventTypes.preTrip',
  'post-trip': 'compliance.overview.calendar.eventTypes.postTrip',
  'pre-work-order': 'compliance.overview.calendar.eventTypes.preWorkOrder',
  'post-work-order': 'compliance.overview.calendar.eventTypes.postWorkOrder',
  periodic: 'compliance.overview.calendar.eventTypes.periodic',
  unknown: null
};

export interface UpdateComplianceCalendarEventScheduleApiRequest {
  taskType: ComplianceTaskType;
  sourceTaskId: number;
  scheduledDate: Date;
  phase?: CompliancePhase | null;
}

export interface ComplianceCalendarEventScheduleApiRequest {
  taskType: ComplianceTaskType;
  sourceTaskId: number;
  phase?: CompliancePhase | null;
}

export type ComplianceCalendarStatus =
  | 'Compliant'
  | 'Non-Compliant'
  | 'In Progress'
  | 'Missed';

export type ComplianceCalendarEventResponse = {
  id: number;
  sourceTaskId: number;
  taskType: ComplianceTaskType;
  phase: CompliancePhase | null;
  calendarEventType: ComplianceCalendarEventType;
  assetId: number;
  assetName: string;
  complianceName: string;
  isMissedEvent: boolean;
  type: ComplianceCalendarStatus;
  scheduledDate: string | Date;
}

export type TodayComplianceCalendarEventResponse = {
  id: number;
  sourceTaskId: number;
  taskType: ComplianceTaskType;
  phase: CompliancePhase | null;
  calendarEventType: ComplianceCalendarEventType;
  assetId: number;
  assetName: string;
  complianceName: string;
  type: ComplianceCalendarStatus;
  startTime: string | Date;
}

export type ComplianceCalendarEventScheduleResponse = {
  assetId: number;
  assetName: string;
  make?: string | null;
  model?: string | null;
  complianceStandard: string;
  checklistType: string;
  calendarEventType: ComplianceCalendarEventType;
  workshop?: string | null;
  scheduledDate: string | Date;
  phase: CompliancePhase | null;
}

export type ComplianceOverviewStatusSummary = Pick<
  ComplianceOverviewRequirement,
  'compliantCount' | 'expiringSoonCount' | 'nonCompliantCount'
>;

export type ComplianceAsset = {
  assetId?: number;
  assetName: string;
  complianceType: string;
  document: string;
  documentExpiry?: string;
  linkedDocumentId?: number | null;
  lastInspectionDate?: string;
  linkedDocument?: RoutineChecklistRequirementLinkedDocument | null;
  actions?: ComplianceTasksTableRowAction[];
  dateIssued: string;
  overdueDays: number | string;
  status: string;
}

export type PeriodicCompliance = {
  assetName: string;
  frequency: string;
  complianceStandard: string;
  workshopName?: string;
  dueDate: string;
  status: string;
}

export enum ComplianceStatus {
  Pending = 1,
  InProgress = 2,
  Completed = 3,
  Overdue = 4,
  Missed = 5
}

export interface PeriodicComplianceTasksRequest {
  accountIds: number[];
  page: number;
  pageSize: number;
  regionId: number | null;
  countryId: number | null;
  complianceId: number | null;
  complianceStatus: ComplianceStatus | null;
  searchTerm: string | null;
}

export interface PeriodicComplianceTask {
  id: number;
  accountId: number;
  assetId: number;
  complianceId?: number;
  assetName: string;
  frequency: string;
  complianceStandard: string;
  workshopName: string | null;
  dueDate: Date;
  complianceStatus: ComplianceStatus;
}

export type PeriodicComplianceTaskRow = PeriodicComplianceTask & {
  /** Display label for `frequency`; `frequency` itself stays the raw backend name. */
  frequencyLabel: string;
  dueDateLabel: string;
  complianceStatusLabel: string;
  accountId: number;
};

export interface EditPeriodicComplianceTaskRequest {
  id: number;
  assetId: number;
  workshopId: number | null;
  frequencyId: number;
}

export interface ReschedulePeriodicComplianceTaskRequest {
  id: number;
  assetId: number;
  dueDate: string;
  notifyPersonResponsible: boolean;
}

export interface RescheduleRoutineComplianceTaskRequest {
  id: number;
  assetId: number;
  dueDate: string;
  phase: CompliancePhase;
}

export interface DeletePeriodicComplianceTaskRequest {
  id: number;
  assetId: number;
}

export interface DeleteRoutineComplianceTaskRequest {
  id: number;
  assetId: number;
}

export interface AssetComplianceChecklistResultsRequest {
  assetId: number;
  complianceId: number;
  taskId: number;
  page: number;
  pageSize: number;
}

export interface AssetComplianceChecklistResult {
  assetId: number;
  submissionId: number;
  checklistId: number;
  checklistName: string;
  frequency: string;
  date: string | Date | null;
  checklistType: string;
  attachmentCount: number;
  notes: string | null;
  submissionStatus: ComplianceResult;
}

export type AssetComplianceChecklistResultRow = AssetComplianceChecklistResult & {
  date: Date | null;
  dateLabel: string;
  /** Display label for `frequency`; `frequency` itself stays the raw backend name. */
  frequencyLabel: string;
  checklistTypeLabel: string;
  attachmentCountLabel: string;
  submissionStatusLabel: string;
};

export interface AssetComplianceChecklistSummaryRequest {
  assetId: number;
  complianceId: number;
  taskId?: number | null;
}

export type RoutineCompliance = {
  assetName: string;
  checklistType: string;
  complianceType: string;
  workshop?: string;
  preDateTime?: string;
  preStatus?: string;
  postDateTime?: string;
  postStatus?: string;
}

export interface RoutineComplianceTasksRequest {
  accountIds: number[];
  page: number;
  pageSize: number;
  regionId: number | null;
  countryId: number | null;
  complianceStatus: ComplianceStatus | null;
  complianceId: number | null;
  assetTypeId: AssetType | null;
  searchTerm: string | null;
}

export interface RoutineComplianceTask {
  id: number;
  accountId: number;
  complianceId: number;
  assetId: number;
  assetName: string;
  routineTypeId: number;
  checklistType: string;
  complianceTask: string;
  workshop: string | null;
  preDateTime: string | null;
  preComplianceStatus: ComplianceStatus | null;
  postDateTime: string | null;
  postComplianceStatus: ComplianceStatus | null;
  preInspector: string | null;
  preInspectorRole: string | null;
  postInspector: string | null;
  postInspectorRole: string | null;
  preCompletedAt: string | null;
  postCompletedAt: string | null;
}

export interface RoutineComplianceTaskLeg {
  phase: CompliancePhase;
  routineTypeLabel: string;
  dueDateTime: string | null;
  workshop: string | null;
  inspectedBy: string | null;
  inspectedByRole: string | null;
  completedOn: string | null;
  complianceStatus: ComplianceStatus | null;
  actions: ComplianceTasksTableRowAction[];
}

export type RoutineComplianceTaskRow = RoutineComplianceTask & {
  /** Display label for `checklistType` (the routine type); `checklistType` itself stays raw for logic. */
  checklistTypeLabel: string;
  legs: RoutineComplianceTaskLeg[];
};

export interface AssignRoutineComplianceTaskWorkshopRequest {
  id: number;
  workshopId: number;
  assetId: number;
}

export type ComplianceRegion = {
  id: number;
  name: string;
  type: 'region' | 'country';
}

export type ComplianceType = {
  id: number;
  name: string;
}

export enum ComplianceStatusFilter {
  All = 0,
  Pending = 1,
  InProgress = 2,
  Completed = 3,
  Overdue = 4,
  Missed = 5
}

/** A single entry in a sub-table row's overflow menu. */
export interface ComplianceTasksTableRowAction {
  label: string;
  action: ComplianceTasksTableAction;
  /** Render the entry in the destructive (red) style. */
  danger?: boolean;
}

export interface TableColumn {
  header: string;
  value: string;
  width: string;
  type: 'text' | 'nullable-text' | 'status' | 'action' | 'checkbox' | 'stacked' | 'date' | 'expand' | 'sub-action' | 'link';
  centered?: boolean;
  secondaryValue?: string;
  truncate?: boolean;
  title?: boolean;
  bold?: boolean;
  mutedValue?: string;
  linkValue?: string;
  subLabelValue?: string;
}

export type ComplianceTaskType = 'periodic' | 'routine';

export type AssetComplianceTask = {
  checklist: string;
  frequency: string;
  date: string;
  docChecklist: string;
  inspectionUploads: string;
  notes: string;
  taskStatus: string;
}